import { createFileRoute, Link } from "@tanstack/react-router";
import { Award, Heart, Users, Sparkles } from "lucide-react";
import Layout from "@/components/Layout";
import detailImg from "@/assets/service-detail-896.jpg";
import detailImg480 from "@/assets/service-detail-480.jpg";
import detailImg800 from "@/assets/service-detail-800.jpg";
import detailImg896 from "@/assets/service-detail-896.jpg";
import detailImg480Webp from "@/assets/service-detail-480.webp";
import detailImg800Webp from "@/assets/service-detail-800.webp";
import detailImg896Webp from "@/assets/service-detail-896.webp";
import detailImg480Avif from "@/assets/service-detail-480.avif";
import detailImg800Avif from "@/assets/service-detail-800.avif";
import detailImg896Avif from "@/assets/service-detail-896.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import { buildBreadcrumbJsonLd, canonicalLink, pageSocialMeta } from "@/lib/seo";

const BREADCRUMB_JSONLD = buildBreadcrumbJsonLd([
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
]);

const ABOUT_TITLE = "About BLVDS Detailing — NYC Mobile Car Detailing";
const ABOUT_DESCRIPTION = "BLVDS Detailing brings showroom-quality mobile car washes, full details, and ceramic coatings to driveways across Queens, Brooklyn & Long Island.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: ABOUT_TITLE },
      { name: "description", content: ABOUT_DESCRIPTION },
      ...pageSocialMeta("/about", ABOUT_TITLE, ABOUT_DESCRIPTION),
    ],
    links: [canonicalLink("/about")],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_JSONLD) },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <Layout>
      <section className="pt-16 pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-3 text-center">About BLVDS</p>
        <h1 className="text-5xl sm:text-6xl font-bold mb-8 text-center">
          Born in NYC. <br /><span className="text-gradient-primary">Built for your car.</span>
        </h1>

        <div className="rounded-2xl overflow-hidden mb-12 border border-border mx-auto max-w-3xl aspect-[896/1200]">
          <ResponsiveImage
            src={detailImg}
            avif={[
              { src: detailImg480Avif, width: 480 },
              { src: detailImg800Avif, width: 800 },
              { src: detailImg896Avif, width: 896 },
            ]}
            webp={[
              { src: detailImg480Webp, width: 480 },
              { src: detailImg800Webp, width: 800 },
              { src: detailImg896Webp, width: 896 },
            ]}
            jpg={[
              { src: detailImg480, width: 480 },
              { src: detailImg800, width: 800 },
              { src: detailImg896, width: 896 },
            ]}
            sizes="(max-width: 768px) 100vw, 768px"
            alt="BLVDS detailing in action"
            className="w-full h-full object-cover"
            width={896}
            height={1200}
          />
        </div>

        <div className="prose prose-invert max-w-none text-lg text-muted-foreground space-y-6">
          <p>
            BLVDS Detailing started with a simple frustration: NYC drivers shouldn't have to sacrifice an entire Saturday to drive across the borough, wait in line, and still end up with swirl marks. We thought you deserved better.
          </p>
          <p>
            Today, we're a team of meticulous, certified detailers who pull up to your driveway with a fully-equipped van premium foam cannons, Gtechniq ceramic coatings, professional polishers, and the kind of attention to detail you used to only find in dealerships. We treat every car like it's our own.
          </p>
          <p>
            From a quick exterior wash before a Sunday drive to a multi-stage paint correction that makes a 5-year-old paint job look fresh off the lot we do it all, and we do it right. <span className="text-foreground font-semibold">No shortcuts. No excuses. Just results.</span>
          </p>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-12">Why Choose <span className="text-gradient-accent inline-block pr-2">BLVDS</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Award, title: "Certified Pros", desc: "IDA-trained detailers with 10+ years combined experience." },
            { icon: Sparkles, title: "Premium Products", desc: "Only the best Meguiar's, Chemical Guys, Gtechniq." },
            { icon: Heart, title: "Care, Not Carwash", desc: "Hand wash, microfiber, two-bucket method. Always." },
            { icon: Users, title: "Real Locals", desc: "Born and raised in NYC. We know your neighborhood." },
          ].map((v) => (
            <div key={v.title} className="p-6 rounded-2xl bg-surface border border-border text-center">
              <div className="w-12 h-12 rounded-xl bg-gradient-primary mx-auto flex items-center justify-center mb-4">
                <v.icon className="w-6 h-6 text-primary-foreground" />
              </div>
              <h3 className="font-bold mb-2">{v.title}</h3>
              <p className="text-sm text-muted-foreground">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl p-10 sm:p-14 bg-gradient-to-br from-primary/15 via-surface to-accent/10 border border-primary/30 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Let's make your car look unreal.</h2>
          <p className="text-muted-foreground mb-7">Book online in under a minute.</p>
          <Link to="/book" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:scale-105 transition-smooth">
            Book Now
          </Link>
        </div>
      </section>
    </Layout>
  );
}
